
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/use-profile';
import ProfileGallery from '@/components/profile/ProfileGallery';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ProfileLandingPage from '@/components/profile/ProfileLandingPage';
import PageLayout from '@/components/layout/PageLayout';
import { cn } from '@/lib/utils';

const UserProfile = () => {
  const { username } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, isLoading, error, refetch } = useProfile(username);
  const [showGallery, setShowGallery] = useState(false);
  const galleryRef = useRef<HTMLDivElement>(null);
  const landingRef = useRef<HTMLDivElement>(null);

  // Enhanced logging for debugging profile issues
  useEffect(() => {
    console.log('UserProfile - State:', {
      username,
      hasUser: !!user,
      userId: user?.id,
      profileId: profile?.id,
      profileUsername: profile?.username,
      displayName: profile?.display_name,
      isLoading,
      error: error ? String(error) : null,
      timestamp: new Date().toISOString()
    });
  }, [username, user, profile, isLoading, error]);

  // Handle temp username navigation with improved error handling
  useEffect(() => {
    if (!username) return;
    
    // Cover both 'user_' pattern and cases where username is "anonymous"
    const isTempOrMissingUsername = username.startsWith('user_') || username === 'anonymous';
    
    if (isTempOrMissingUsername && user?.id) {
      console.log('Checking if we need to redirect from temporary username', {
        currentUsername: username, 
        profileUsername: profile?.username,
        profileId: profile?.id,
        userId: user.id
      });
      
      // If this is a temporary username and we're the logged-in user
      if (profile && user.id === profile.id) {
        console.log('Redirecting from temporary username to profile page');
        navigate('/profile', { replace: true });
        return;
      }
    }
  }, [username, user, profile, navigate]);

  // Enhanced refetch logic for profile data
  useEffect(() => {
    if (!username) return;
    
    // Immediate refetch when component mounts
    console.log('UserProfile - Refetching profile data on mount for:', username);
    refetch().catch(err => 
      console.error('Error refetching profile on mount:', err)
    );
    
    // Set up refresh listeners
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('UserProfile - Page became visible, refreshing profile for:', username);
        queryClient.invalidateQueries({ queryKey: ['profile', username] });
        queryClient.invalidateQueries({ queryKey: ['follow-status'] });
      }
    };

    const handleFocus = () => {
      console.log('UserProfile - Window focused, refreshing profile for:', username);
      queryClient.invalidateQueries({ queryKey: ['profile', username] });
      queryClient.invalidateQueries({ queryKey: ['follow-status'] });
    };
    
    // Listen for window focus/visibility events
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // Optional: set up a polling interval for very active profiles
    const intervalId = setInterval(() => {
      console.log('UserProfile - Polling refresh for:', username);
      queryClient.invalidateQueries({ queryKey: ['profile', username] });
      queryClient.invalidateQueries({ queryKey: ['follow-status'] });
    }, 30000); // Every 30 seconds
    
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, [username, refetch, queryClient]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (error || !profile) {
    // Improve error display with more helpful information
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-2xl font-bold mb-4">Profile not found</h1>
        <p className="text-gray-400">The requested profile could not be found</p>
        {error && (
          <p className="text-red-400 mt-2">
            Error: {error instanceof Error ? error.message : String(error)}
          </p>
        )}
        {username?.startsWith('user_') && (
          <p className="text-gray-400 mt-2">
            This appears to be a temporary username. The user may need to complete their profile setup.
          </p>
        )}
        {username === 'anonymous' && (
          <p className="text-gray-400 mt-2">
            This appears to be a placeholder username. The actual profile might be using a different username.
          </p>
        )}
      </div>
    );
  }

  const handleViewGallery = () => {
    setShowGallery(true);
    setTimeout(() => {
      galleryRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleScrollToLanding = () => {
    if (landingRef.current) {
      landingRef.current.scrollIntoView({ behavior: 'smooth' });
      setShowGallery(false);
    }
  };

  return (
    <PageLayout transparentHeader={true}>
      <div className="bg-[#1A1F2C] absolute top-0 left-0 right-0 bottom-0 w-full overflow-auto" style={{ scrollBehavior: 'smooth', scrollSnapType: 'y proximity' }}>
        {/* Landing Page Section */}
        <div ref={landingRef}>
          <ProfileLandingPage 
            profile={profile} 
            onViewGallery={handleViewGallery}
          />
        </div>

        {/* Gallery Section */}
        <AnimatePresence>
          {showGallery && (
            <motion.div
              ref={galleryRef}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="bg-[#111418] px-4 md:px-6 py-12"
            >
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-white">Gallery</h2>
                  <button
                    onClick={handleScrollToLanding}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200 bg-white/10 px-3 py-1 rounded-full hover:bg-white/20"
                  >
                    Back to Profile
                  </button>
                </div>
                <ProfileGallery userId={profile.id} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PageLayout>
  );
};

export default UserProfile;
